import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { X, Info, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { announcementsAPI } from '@/lib/api';

const ANNOUNCEMENT_ICONS = {
    info: Info,
    warning: AlertTriangle,
    success: CheckCircle,
    error: XCircle,
};

const ANNOUNCEMENT_STYLES = {
    info: 'border-blue-200 bg-blue-50 text-blue-800 [&>svg]:text-blue-600',
    warning: 'border-yellow-200 bg-yellow-50 text-yellow-800 [&>svg]:text-yellow-600',
    success: 'border-green-200 bg-green-50 text-green-800 [&>svg]:text-green-600',
    error: 'border-red-200 bg-red-50 text-red-800 [&>svg]:text-red-600',
};

const FrontpageAnnouncements = () => {
    const [announcements, setAnnouncements] = useState([]);
    const [dismissedIds, setDismissedIds] = useState(() => {
        // Load dismissed announcements from localStorage
        const saved = localStorage.getItem('dismissedAnnouncements');
        return saved ? JSON.parse(saved) : [];
    });
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const response = await announcementsAPI.getActive();
                setAnnouncements(response.data || []);
            } catch (error) {
                console.error('Error fetching announcements:', error);
            } finally {
                setLoading(false);
            }
        };
        
        fetchAnnouncements();
    }, []);
    
    const handleDismiss = (announcementId) => {
        const newDismissed = [...dismissedIds, announcementId];
        setDismissedIds(newDismissed);
        localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
    };
    
    // Filter out dismissed announcements
    const visibleAnnouncements = announcements.filter(
        ann => !dismissedIds.includes(ann.announcement_id)
    );
    
    if (loading || visibleAnnouncements.length === 0) {
        return null;
    }
    
    return (
        <div className="container mx-auto px-4 lg:px-8 pt-6">
            <div className="space-y-3 mb-6" data-testid="frontpage-announcements">
            {visibleAnnouncements.map((announcement) => {
                const Icon = ANNOUNCEMENT_ICONS[announcement.type] || Info;
                const style = ANNOUNCEMENT_STYLES[announcement.type] || ANNOUNCEMENT_STYLES.info;
                
                return (
                    <Alert 
                        key={announcement.announcement_id}
                        className={`relative ${style}`}
                    >
                        <Icon className="h-4 w-4" />
                        <AlertTitle className="font-semibold pr-8">{announcement.title}</AlertTitle>
                        <AlertDescription className="whitespace-pre-wrap">
                            {announcement.content}
                        </AlertDescription>
                        <button
                            type="button"
                            className="absolute top-3 right-3 opacity-50 hover:opacity-100 transition-opacity"
                            onClick={() => handleDismiss(announcement.announcement_id)}
                            aria-label="Închide"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </Alert>
                );
            })}
            </div>
        </div>
    );
};

export default FrontpageAnnouncements;
